export type FilterOptionValue = string | number | boolean;

/** One selected option as the FilterBar reports it, e.g. `{ id, name }` or `{ value, label }`. */
export type FilterOption = object;

/** FilterBar selections keyed by filter name: an array for a multi-select filter, one option (or null once cleared) for a single-select. */
export type FilterSelections = Record<string, FilterOption | FilterOption[] | null>;

export interface FilterKeyConfig {
  /** Option property that becomes the request value. */
  value: string;
  /** Option property holding the display label, for filters whose options are fetched rather than static. */
  label?: string;
}

/** Per-page table of the filters a list sends to the API and how to read each option. */
export type FilterKeys = Record<string, FilterKeyConfig>;

export type FilterParams = Record<string, FilterOptionValue[]>;

/** Display labels keyed by filter name then request value. */
export type FilterLabels = Record<string, Record<string, string>>;

const isOptionValue = (value: unknown): value is FilterOptionValue =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

/** Maps FilterBar selections to request values, dropping filters that are not in `keys`. */
export const toFilterParams = (selections: FilterSelections, keys: FilterKeys): FilterParams =>
  Object.entries(selections).reduce<FilterParams>((params, [name, options]) => {
    const config = keys[name];
    if (config)
      params[name] = [options]
        .flat()
        .map(option => option && Reflect.get(option, config.value))
        .filter(isOptionValue);
    return params;
  }, {});

/** Collects display labels for the filters in `keys` that declare a label property. */
export const toFilterLabels = (selections: FilterSelections, keys: FilterKeys): FilterLabels =>
  Object.entries(selections).reduce<FilterLabels>((labels, [name, options]) => {
    const config = keys[name];
    if (!config?.label) return labels;
    const labelKey = config.label;
    labels[name] = [options].flat().reduce<Record<string, string>>((byValue, option) => {
      if (!option) return byValue;
      const value: unknown = Reflect.get(option, config.value);
      const label: unknown = Reflect.get(option, labelKey);
      if (isOptionValue(value) && typeof label === 'string') byValue[String(value)] = label;
      return byValue;
    }, {});
    return labels;
  }, {});

/**
 * Rebuilds FilterBar initial values from request values. Filters with static options pass their raw
 * values through for the FilterBar to resolve; filters with a label property become option objects,
 * falling back to the value itself when no label is known.
 */
export const toInitialFilters = (
  values: Record<string, string[]>,
  keys: FilterKeys,
  labels: FilterLabels = {}
): Record<string, unknown> =>
  Object.entries(values).reduce<Record<string, unknown>>((initial, [name, list]) => {
    const config = keys[name];
    if (!config) return initial;
    initial[name] = config.label
      ? list.map(value => ({ [config.value]: value, [config.label as string]: labels[name]?.[value] ?? value }))
      : list;
    return initial;
  }, {});
